/* eslint-disable prefer-const */
import qs from "query-string";
import Router from "next/router";
import publicConfig from "../../server/public-config";
import { logFrontEndError } from "@lululemon/ecom-site-utils/src/logger/utils";
import LocaleManager from "@lululemon/ecom-site-utils/src/utils/locale-manager";

const canUseDOM = typeof document !== "undefined";

function isRelativeURI(url) {
  return !url.startsWith("http");
}

export const omitQueryFromUrl = (url, omitArray = []) => {
  let { url: parsedUrl, query } = qs.parseUrl(url);
  if (omitArray) {
    omitArray.forEach((q) => {
      delete query[q];
    });
  }
  const reconstructedURL = qs.stringifyUrl(
    { url: parsedUrl, query },
    { encode: false }
  );
  return reconstructedURL;
};

const redirect = (path, res, locale) => {
  const { isRAEnabled, defaultLocale, nextLocale } = locale || {};
  return new Promise((resolve, reject) => {
    const redirectPath = LocaleManager.getLocalizedPath({ path });

    if (res) {
      res.writeHead(302, {
        Location: isRelativeURI(redirectPath)
          ? `${publicConfig.host}${redirectPath}`
          : redirectPath
      });
      res.end((error) => {
        if (error) {
          reject(error);
        }

        resolve();
      });
      return;
    }

    if (!isRelativeURI(redirectPath) && canUseDOM) {
      // the forceLogin query param can cause unintentional logout;
      // use replace history to prevent a guest clicking "Back" to
      // the login page and accidentally forcing another logout
      if (/forceLogin/.test(redirectPath)) {
        const cleanedPath = omitQueryFromUrl(redirectPath, ["forceLogin"]);
        // eslint-disable-next-line consistent-return
        return window.location.replace(cleanedPath);
      }

      window.location.href = redirectPath;
      // Do not resolve promise as a full page load
      // to the destination will occur
      return;
    }

    try {
      if (isRAEnabled && defaultLocale !== nextLocale) {
        Router.push(path, undefined, { locale: nextLocale });
      } else {
        Router.push(path);
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  }).catch((error) => {
    // Capture exception in Datadog and re-throw for downstream
    // handling in application

    logFrontEndError({
      error,
      type: "error",
      message: "Failed to redirect",
      tags: {
        functionName: "redirect"
      },
      customTags: {
        path
      }
    });

    throw error;
  });
};

export const getHostURL = () => {
  return publicConfig.host;
};

export const createRedirectUrl = (
  url = "",
  { redirectParamName = "", query: params = {} } = {}
) => {
  let remappedUrl;

  if (params) {
    remappedUrl = qs.stringifyUrl({ url, query: params });
  }

  let { url: parsedUrl, query } = qs.parseUrl(remappedUrl || url);
  const paramRedirectURI = query[redirectParamName];
  delete query[redirectParamName];

  // sometimes redirects already have some query params in them.
  if (paramRedirectURI) {
    // paramRedirectURI is double encoded-- once from the original
    // query param which is encoded, and then again by next.js,
    // which encodes query parameters as well
    const decodedRedirectURI = decodeURIComponent(
      decodeURIComponent(paramRedirectURI)
    );

    const { url: redirectUrl, query: redirectQuery } =
      qs.parseUrl(decodedRedirectURI);

    if (isRelativeURI(redirectUrl)) {
      const pageUrl = getHostURL();
      parsedUrl = `${pageUrl}${decodedRedirectURI}`;
    } else {
      parsedUrl = redirectUrl;
    }
    query = { ...query, ...redirectQuery };
  }
  // remove "redirectParamName" from continuing being
  const { page, ...parsedQuery } = query;

  const reconstructedURL = qs.stringifyUrl(
    { url: parsedUrl, query: parsedQuery },
    { encode: false } // friendly for our ";" in our mnid' query params.
  );

  return reconstructedURL;
};

export default redirect;
